import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { toast } from "@/components/ui/use-toast";
import { phpApi } from "@/integrations/php/client";
import { usePartners } from "@/context/PartnerContext";

const formSchema = z.object({
  email: z.string().email({
    message: "Παρακαλώ εισάγετε ένα έγκυρο email.",
  }),
  phone: z.string().min(10, {
    message: "Το τηλέφωνο πρέπει να έχει τουλάχιστον 10 ψηφία.",
  }),
});

const PartnerLogin = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { partners } = usePartners();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      phone: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    
    try {
      // Find the partner with matching email and phone
      const matchedPartner = partners.find(
        (partner) => 
          partner.email.toLowerCase() === values.email.toLowerCase() && 
          partner.phone === values.phone
      );
      
      if (matchedPartner) {
        // Store partner ID in session storage for temporary access
        sessionStorage.setItem("partnerId", matchedPartner.id);
        sessionStorage.setItem("partnerName", matchedPartner.name);
        
        // We'll skip updating the last_login time for now as the field doesn't exist in the schema
        
        toast({
          title: "Επιτυχής σύνδεση!",
          description: `Καλωσήρθατε, ${matchedPartner.name}`,
        });
        
        // Redirect to partner dashboard
        navigate(`/partner/${matchedPartner.id}/dashboard`);
      } else {
        toast({
          title: "Σφάλμα σύνδεσης",
          description: "Τα στοιχεία που εισάγατε δεν αντιστοιχούν σε κάποιον συνεργάτη.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Login error:", error);
      toast({
        title: "Σφάλμα σύνδεσης",
        description: "Προέκυψε σφάλμα κατά τη σύνδεση. Παρακαλώ δοκιμάστε ξανά.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="w-full max-w-md p-4">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold mb-2">Πύλη Συνεργατών</h1>
          <p className="text-sm text-muted-foreground">Συνδεθείτε με το email και το τηλέφωνό σας</p>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle>Σύνδεση Συνεργάτη</CardTitle>
            <CardDescription>
              Εισάγετε τα στοιχεία σας για να δείτε τις αναθέσεις σας
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input placeholder="your@email.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Τηλέφωνο</FormLabel>
                      <FormControl>
                        <Input placeholder="6912345678" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? (
                    <div className="flex items-center space-x-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                      <span>Σύνδεση...</span>
                    </div>
                  ) : (
                    "Σύνδεση"
                  )}
                </Button>
              </form>
            </Form>
          </CardContent>
          <CardFooter className="flex justify-center border-t pt-4">
            <p className="text-xs text-muted-foreground">
              Επικοινωνήστε με τον διαχειριστή εάν έχετε πρόβλημα σύνδεσης
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default PartnerLogin;
